<template>
    <div class="position-relative">
        <div v-if="hasRooms" class="row g-3">
            <div
                v-for="room in rooms"
                :key="room.service_general_id"
                class="col-12 col-sm-6 col-md-4 col-lg-3"
            >
                <div class="room-card h-100" @click="onClickCard(room)">
                    <div class="status-accent"></div>
                    <div class="room-card-body">
                        <div class="room-head">
                            <div class="room-icon">
                                <i class="fa fa-bed"></i>
                            </div>
                            <div class="room-head-text">
                                <div class="room-name" :title="room.room_name">{{ room.room_name }}</div>
                                <span class="status-pill">
                                    <i class="fa fa-broom me-1"></i>Dirty
                                </span>
                            </div>
                        </div>
                        <div class="reservation-row">
                            <i class="fa fa-hashtag me-2"></i>
                            <span class="reservation-no" :title="room.reservation_no">{{ reservationLabel(room.reservation_no) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!hasRooms && !loading" class="empty-state">
            No dirty room found
        </div>

        <div class="mt-3">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>

        <HousekeepingModal
            v-if="showModal"
            :room="selectedRoom"
            :allow-inspection="true"
            @onClose="showModal = false"
            @onDone="onDone"
        />

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import HousekeepingModal from '@/components/molecule/company/hotel/hotel-housekeeping/HousekeepingModal.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const { fetchHousekeepingList } = handleHotel();

const offset = ref(20);
const loading = ref(false);
const rooms = ref([]);
const showModal = ref(false);
const selectedRoom = ref(null);

const hasRooms = computed(() => rooms.value.length > 0);

const getQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    query += '&completion_status=dirty';
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + offset.value;
    return query;
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const getRoomList = async () => {
    loading.value = true;
    rooms.value = [];
    await fetchHousekeepingList(getQuery()).then(res => {
        if (!res.status) {
            $store.commit('resetPagination');
            return;
        }
        rooms.value = res.data.data;
        setPagination(res.data);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query, { page });
    await $router.push({ path: $route.path, query: routeQuery });
    await getRoomList();
};

const reservationLabel = (reservationNo) => {
    if (!reservationNo) return 'No reservation';
    return reservationNo;
};

const onClickCard = (room) => {
    selectedRoom.value = room;
    showModal.value = true;
};

const onDone = () => {
    showModal.value = false;
    getRoomList();
};

onMounted(() => {
    getRoomList();
});
</script>

<style scoped>
.room-card {
    position: relative;
    background: #ffffff;
    border: 1px solid #eceef1;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
    cursor: pointer;
}
.room-card:hover {
    box-shadow: 0 14px 32px rgba(217, 145, 23, 0.18);
    transform: translateY(-4px);
    border-color: #f0d9a8;
}
.status-accent {
    height: 6px;
    background: linear-gradient(90deg, #d99117, #f0b64a);
}
.room-card-body {
    padding: 18px 18px 16px;
}
.room-head {
    display: flex;
    align-items: center;
    gap: 12px;
}
.room-icon {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b7791f;
    background: linear-gradient(135deg, #fbf0da, #f7e3b8);
}
.room-icon .fa-bed {
    font-size: 1.2rem;
}
.room-head-text {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.room-name {
    font-size: 1.05rem;
    font-weight: 700;
    color: #101828;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.status-pill {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b7791f;
    background: #fdf1dc;
}
.reservation-row {
    display: flex;
    align-items: center;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed #eceef1;
    font-size: 0.85rem;
    color: #475467;
}
.reservation-row .fa-hashtag {
    color: #b0b7c3;
    font-size: 0.78rem;
}
.reservation-no {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.empty-state {
    text-align: center;
    color: #98a2b3;
    padding: 60px 0;
    font-size: 0.95rem;
}
.empty-state .fa-broom {
    font-size: 2.2rem;
    opacity: 0.4;
}
</style>
